import { Component, ElementRef, inject, signal, ViewChild, WritableSignal, computed, effect } from '@angular/core';
import { NgIf, UpperCasePipe } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { ChallengesTypes } from '../../../shared/enums/challenges-types.enum';
import { ModalService } from '../../../shared/services/modal.service';
import { ChallengeService } from '../../../shared/services/challenge.service';
import { ChallengeQuizQuestion, SubmitChallengePayload } from '../../../shared/interfaces/challenge-response.interface';
import { HttpResourceRef } from '@angular/common/http';

type AnswerMap = Record<string, { answerId?: string; text?: string }>;

@Component({
  selector: 'app-mission-quiz-modal',
  standalone: true,
  imports: [UpperCasePipe, NgIf],
  templateUrl: './mission-quiz-modal.component.html',
  styles: ``
})
export class MissionQuizModalComponent {
  private activeModal = inject(NgbActiveModal);
  private modalService = inject(ModalService);
  private challengeService = inject(ChallengeService);
  private readonly IMAGE_QUESTION_DESC = 'IMAGEN';

  postTypes: typeof ChallengesTypes = ChallengesTypes;


  constructor() {
    effect(() => {
      const ch = this.challenge();
      if (!ch) return;

      this.uploadedImageId.set(null);
      this.selectedFile.set(null);
      this.answersMap.set({});
      this.currentIndex.set(0);

      this.initFlow();
    });
  }


  challenge = signal<any>(null);

  step = signal<1 | 2>(1);

  currentIndex = signal(0);

  uploadedImageId = signal<string | null>(null);

  sending = signal(false);
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  previewSrc: WritableSignal<string> = signal('assets/images/mision-foto.png');
  showFormatHint: WritableSignal<boolean> = signal(true);
  showUploadHint: WritableSignal<boolean> = signal(false);
  selectedFile: WritableSignal<File | null> = signal(null);

  private readonly maxSizeMB = 12;
  private readonly allowedTypes = new Set([
    'image/jpeg',
    'image/png',
    'image/gif',
    'video/mp4',
    'video/webm',
    'application/pdf'
  ]);

  quizRes: HttpResourceRef<ChallengeQuizQuestion[] | undefined> =
    this.challengeService.getQuizChallengesResource(() => this.challenge()?.id);

  quiz = () => this.quizRes.value() ?? [];
  loadingQuiz = () => this.quizRes.isLoading();

  answersMap = signal<AnswerMap>({});
  private typeIdLower = () => (this.challenge()?.challengeTypeId ?? '').toLowerCase();
  private isImageQuestion(q: ChallengeQuizQuestion): boolean {
    return (q.description ?? '').trim().toUpperCase() === this.IMAGE_QUESTION_DESC;
  }

  displayQuiz = computed(() => {
    const all = this.quiz();
    if (!all.length) return [];

    if (this.isMixed()) {
      return all.filter(q => !this.isImageQuestion(q));
    }

    return all;
  });
  isImageOnly = () => this.typeIdLower() === this.postTypes.Image.toLowerCase();
  isMixed = () => this.typeIdLower() === this.postTypes.Mixed.toLowerCase();
  isSurvey = () => this.typeIdLower() === this.postTypes.Survey.toLowerCase();
  isSatisfaction = () => this.typeIdLower() === this.postTypes.SatisfactionSurvey.toLowerCase();
  isPermanent = () => this.typeIdLower() === this.postTypes.Permanent.toLowerCase();
  requiresFile = () => this.isImageOnly() || this.isMixed() || this.isPermanent();
  requiresQuiz = () => !this.isMixedCompleted() && (this.isMixed() || this.isSurvey() || this.isSatisfaction());

  initFlow(): void {
    if (this.requiresFile()) this.step.set(1);
    else this.step.set(2);
  }

  currentQuestion = computed(() => {
    const q = this.displayQuiz();
    const idx = this.currentIndex();
    return q.length ? q[idx] : null;
  });

  isLastQuestion = () => this.currentIndex() >= (this.displayQuiz().length - 1);
  isCompletedMechanic = () => (this.challenge()?.status_Mechanic ?? '').trim().toLowerCase() === 'completada';

  isMixedCompleted = () => this.isMixed() && this.isCompletedMechanic();

  getClosedAnswerValue(questionId: string): string {
    return this.answersMap()[questionId]?.answerId ?? '';
  }

  getOpenAnswerValue(questionId: string): string {
    return this.answersMap()[questionId]?.text ?? '';
  }

  setClosedAnswer(questionId: string, answerId: string) {
    const q = this.quiz().find(x => x.questionId === questionId);
    const selected = q?.answers?.find(a => a.answerId === answerId);

    this.answersMap.update(m => ({
      ...m,
      [questionId]: {
        answerId,
        text: selected?.description ?? '' 
      }
    }));
  }

  setOpenAnswer(questionId: string, text: string) {
    this.answersMap.update(m => ({ ...m, [questionId]: { text } }));
  }

  private validateCurrentQuestion(): string | null {
    const q = this.currentQuestion();
    if (!q) return 'No hay pregunta actual.';

    const v = this.answersMap()[q.questionId];
    if (!v) return 'Debes contestar la pregunta para continuar.';

    if (q.isOpen) {
      if (!v.text || !v.text.trim()) return 'Debes contestar la pregunta para continuar.';
    } else {
      if (!v.answerId) return 'Selecciona una opción para continuar.';
    }

    return null;
  }

  nextQuestion(): void {
    const err = this.validateCurrentQuestion();
    if (err) return this.modalService.showErrorModal({ message: err } as any);

    if (this.isLastQuestion()) return;
    this.currentIndex.update(i => i + 1);
  }

  openFilePicker(): void {
    this.fileInputRef?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    if (!this.allowedTypes.has(file.type)) {
      alert('Formato no válido. Por favor sube una imagen, video o PDF.');
      input.value = '';
      this.selectedFile.set(null);
      return;
    }

    if (file.size > this.maxSizeMB * 1024 * 1024) {
      alert('El archivo supera el peso máximo de 12 MB.');
      input.value = '';
      this.selectedFile.set(null);
      return;
    }

    this.selectedFile.set(file);

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        this.previewSrc.set(String(reader.result));
        this.showFormatHint.set(false);
        this.showUploadHint.set(true);
      };
      reader.readAsDataURL(file);
      return;
    }

    if (file.type === 'application/pdf') {
      this.previewSrc.set('assets/images/pdf-icon.png');
    } else if (file.type.startsWith('video/')) {
      this.previewSrc.set('assets/images/video-icon.png');
    }

    this.showFormatHint.set(false);
    this.showUploadHint.set(true);
  }

  private buildQuizPayload() {
    const map = this.answersMap();

    return this.displayQuiz().map(q => {
      const v = map[q.questionId] ?? {};

      const answerText = (v.text ?? '').trim();
      const isEmpty = answerText.length === 0;

      return {
        questionId: q.questionId,
        answerId: v.answerId ?? null,
        answer: isEmpty ? null : answerText,
        isImage: this.isImageQuestion(q)
      };
    });
  }


  uploadFileAndContinue(): void {
    if (!this.selectedFile()) {
      return this.modalService.showErrorModal({ message: 'Debes seleccionar un archivo.' } as any);
    }

    const file = this.selectedFile()!;
    const fd = new FormData();
    fd.append('request', file, file.name);

    this.sending.set(true);
    this.challengeService.uploadChallengeFile(fd).subscribe({
      next: (imageId) => {
        this.sending.set(false);
        this.uploadedImageId.set(imageId ?? null);
        const imgQid = this.findImageQuestionId();
        if (imgQid) {
          this.answersMap.update(m => ({
            ...m,
            [imgQid]: { text: 'Imagen' }
          }));
        }
        if (this.isMixedCompleted() || this.isImageOnly() || this.isPermanent()) {
          this.submitFinal();
          return;
        }

        this.step.set(2);
        this.currentIndex.set(0);
      },
      error: (e) => {
        this.sending.set(false);
        this.modalService.showErrorModal(e);
      }
    });
  }

  submitFinal(): void {
    if (this.isMixedCompleted()) {
      if (!this.uploadedImageId()) {
        return this.modalService.showErrorModal({ message: 'Debes subir un archivo para continuar.' } as any);
      }

      const imgQid = this.findImageQuestionId();
      if (!imgQid) {
        return this.modalService.showErrorModal({ message: 'No se encontró la pregunta Imagen para este desafío.' } as any);
      }

      const challengeId = this.challenge()?.id as string;
      const challengeTypeId = this.challenge()?.challengeTypeId as string;

      const payload: SubmitChallengePayload = {
        challengeId,
        challengeImageId: this.uploadedImageId(),
        challengeTypeId,
        quizAnswers: [{ questionId: imgQid, answerId: null, answer: 'Imagen', isImage: true }] 
      };

      this.sending.set(true);
      this.challengeService.submitChallenge(payload).subscribe({
        next: () => {
          this.sending.set(false);
          this.modalService.openSuccessMission('Tu imagen se guardó correctamente.');
          this.activeModal.close(true);
        },
        error: (e) => {
          this.sending.set(false);
          this.modalService.showErrorModal(e);
        }
      });

      return;
    }
    if (this.requiresQuiz()) {
      if (this.loadingQuiz()) {
        return this.modalService.showErrorModal({ message: 'Aún se están cargando las preguntas.' } as any);
      }
      if (!this.quiz().length) {
        return this.modalService.showErrorModal({ message: 'No se encontraron preguntas para este desafío.' } as any);
      }

      const err = this.validateCurrentQuestion();
      if (err) return this.modalService.showErrorModal({ message: err } as any);

      const map = this.answersMap();
      for (const q of this.displayQuiz()) {
        const v = map[q.questionId];
        if (!v) return this.modalService.showErrorModal({ message: 'Debes contestar todas las preguntas.' } as any);
        if (q.isOpen && (!v.text || !v.text.trim()))
          return this.modalService.showErrorModal({ message: 'Debes contestar todas las preguntas.' } as any);
        if (!q.isOpen && !v.answerId)
          return this.modalService.showErrorModal({ message: 'Debes contestar todas las preguntas.' } as any);
      }
    }

    if (this.requiresFile() && !this.uploadedImageId() && !this.isSurvey() && !this.isSatisfaction()) {
      return this.modalService.showErrorModal({ message: 'Debes subir un archivo para continuar.' } as any);
    }

    const challengeId = this.challenge()?.id as string;
    const challengeTypeId = this.challenge()?.challengeTypeId as string;
    const imgQid = this.findImageQuestionId();
    let quizAnswers = this.requiresQuiz() ? this.buildQuizPayload() : [];
    if (this.requiresFile() && imgQid) {
      const already = quizAnswers.some(x => x.questionId === imgQid);
      if (!already) {
        quizAnswers = [...quizAnswers, { questionId: imgQid, answerId: null, answer: 'Imagen', isImage: true }];
      }
    }
    const payload: SubmitChallengePayload = {
      challengeId,
      challengeImageId: this.uploadedImageId(),
      challengeTypeId,
      quizAnswers
    };

    this.sending.set(true);
    this.challengeService.submitChallenge(payload).subscribe({
      next: () => {
        this.sending.set(false);
        this.modalService.openSuccessMission('Tu desafío ha sido guardado y completado exitosamente.');
        this.activeModal.close(true);
      },
      error: (e) => {
        this.sending.set(false);
        this.modalService.showErrorModal(e);
      }
    });
  }

  private findImageQuestionId(): string | null {
    const q = this.quiz().find(x => (x.description ?? '').trim().toUpperCase() === this.IMAGE_QUESTION_DESC);
    return q?.questionId ?? null;
  }

  dismiss(): void {
    this.activeModal.dismiss('');
  }
}
